import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Upload, Loader2, FolderPlus } from "lucide-react";
import api, { imgUrl } from "@/lib/api";

const empty = { title: "", category: "", alt_text: "", seo_name: "" };

export default function PortfolioAdmin() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("all");
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkCat, setBulkCat] = useState("");
  const fileRef = useRef();

  const load = () => {
    api.get("/portfolio").then((r) => setItems(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, category: categories[0]?.slug || "" });
    setFile(null); setPreview(null); setModal(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ title: it.title, category: it.category, alt_text: it.alt_text || "", seo_name: "" });
    setFile(null); setPreview(it.image_url); setModal(true);
  };

  const onFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (!form.seo_name && !editing) setForm((s) => ({ ...s, seo_name: f.name.replace(/\.[^.]+$/, "") }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editing && !file) return toast.error("Ajoutez une image.");
    if (!form.title.trim()) return toast.error("Le titre est requis.");
    setSaving(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("category", form.category);
    fd.append("alt_text", form.alt_text);
    fd.append("seo_name", form.seo_name);
    if (file) fd.append("file", file);
    try {
      if (editing) await api.put(`/admin/portfolio/${editing.id}`, fd);
      else await api.post("/admin/portfolio", fd);
      toast.success(editing ? "Réalisation mise à jour" : "Réalisation ajoutée (image compressée en WebP)");
      setModal(false); load();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réalisation ?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    load();
  };

  const toggleSel = (id) => {
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const clearSel = () => setSelected(new Set());

  const bulkDelete = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    if (!window.confirm(`Supprimer ${ids.length} réalisation(s) ?`)) return;
    try {
      await api.post("/admin/portfolio/bulk-delete", { ids });
      toast.success(`${ids.length} réalisation(s) supprimée(s)`);
      clearSel(); load();
    } catch { toast.error("Erreur lors de la suppression"); }
  };

  const bulkRecategorize = async () => {
    const ids = [...selected];
    if (!ids.length || !bulkCat) return;
    try {
      await api.post("/admin/portfolio/bulk-category", { ids, category: bulkCat });
      const catName = categories.find((c) => c.slug === bulkCat)?.name || bulkCat;
      toast.success(`${ids.length} réalisation(s) classée(s) dans « ${catName} »`);
      clearSel(); setBulkCat(""); load();
    } catch { toast.error("Erreur lors du classement"); }
  };

  const deleteCategory = async (cat) => {
    if (!window.confirm(`Supprimer la catégorie « ${cat.name} » ? Les réalisations ne seront pas supprimées.`)) return;
    try {
      await api.delete(`/admin/categories/${cat.id}`);
      if (active === cat.slug) setActive("all");
      toast.success("Catégorie supprimée");
      api.get("/categories").then((r) => setCategories(r.data));
    } catch { toast.error("Impossible de supprimer la catégorie"); }
  };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    try {
      await api.post("/admin/categories", { name: newCat });
      setNewCat(""); toast.success("Catégorie créée");
      api.get("/categories").then((r) => setCategories(r.data));
    } catch (e) { toast.error("Catégorie déjà existante"); }
  };

  const changeCategory = async (id, category) => {
    const prev = items;
    setItems((its) => its.map((i) => (i.id === id ? { ...i, category } : i)));
    try {
      const fd = new FormData();
      fd.append("category", category);
      await api.put(`/admin/portfolio/${id}`, fd);
      const catName = categories.find((c) => c.slug === category)?.name || category;
      toast.success(`Réalisation classée dans « ${catName} »`);
    } catch (e) {
      setItems(prev);
      toast.error("Impossible de changer la catégorie");
    }
  };

  const shown = active === "all" ? items : items.filter((i) => i.category === active);
  const inputCls = "w-full bg-zinc-900 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-yellow-400 focus:outline-none";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Portfolio</h1>
          <p className="text-zinc-500 text-sm mt-1">{items.length} réalisations · upload avec compression & SEO</p>
        </div>
        <button data-testid="portfolio-add" onClick={openNew} className="flex items-center gap-2 bg-yellow-400 text-black font-bold px-4 py-2.5 rounded-md hover:bg-yellow-300">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setActive("all")} className={`px-4 py-2 rounded-full text-sm ${active === "all" ? "bg-yellow-400 text-black" : "bg-white/5 text-zinc-400 border border-white/10"}`}>Tout</button>
        {categories.map((c) => (
          <span key={c.slug} className={`group inline-flex items-center gap-1.5 pl-4 pr-2 py-2 rounded-full text-sm ${active === c.slug ? "bg-yellow-400 text-black" : "bg-white/5 text-zinc-400 border border-white/10"}`}>
            <button data-testid="category-filter" onClick={() => setActive(c.slug)}>{c.name}</button>
            <button data-testid="category-delete" onClick={() => deleteCategory(c)} title="Supprimer la catégorie" className={`p-0.5 rounded-full opacity-40 hover:opacity-100 ${active === c.slug ? "hover:bg-black/20" : "hover:bg-white/10"}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <input data-testid="new-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Nouvelle catégorie" className="bg-zinc-900 border border-white/10 rounded-md px-3 py-1.5 text-sm w-40" />
          <button data-testid="new-category-add" onClick={addCategory} className="p-2 rounded-md bg-white/5 hover:bg-white/10"><FolderPlus className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
          <input
            data-testid="portfolio-select-all"
            type="checkbox"
            checked={shown.length > 0 && shown.every((i) => selected.has(i.id))}
            onChange={(e) => setSelected(e.target.checked ? new Set(shown.map((i) => i.id)) : new Set())}
            className="accent-yellow-400 w-4 h-4"
          />
          Tout sélectionner
        </label>
        {selected.size > 0 && (
          <div data-testid="portfolio-bulk-toolbar" className="flex flex-wrap items-center gap-2 bg-[#18181B] border border-white/10 rounded-lg px-3 py-2">
            <span className="text-sm text-yellow-400 font-semibold">{selected.size} sélectionné(s)</span>
            <select data-testid="bulk-category-select" value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} className="bg-zinc-900 border border-white/10 rounded-md px-2 py-1.5 text-sm">
              <option value="">Classer dans…</option>
              {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select>
            <button data-testid="bulk-category-apply" onClick={bulkRecategorize} disabled={!bulkCat} className="px-3 py-1.5 rounded-md text-sm bg-white/10 hover:bg-white/15 disabled:opacity-40">Classer</button>
            <button data-testid="bulk-delete" onClick={bulkDelete} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-red-500/15 text-red-400 hover:bg-red-500 hover:text-white">
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </button>
            <button data-testid="bulk-clear" onClick={clearSel} className="px-2 py-1.5 rounded-md text-sm text-zinc-400 hover:text-white">Annuler</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {shown.map((it) => (
          <div key={it.id} data-testid="admin-portfolio-item" className={`rounded-xl overflow-hidden bg-zinc-900 border flex flex-col ${selected.has(it.id) ? "border-yellow-400" : "border-white/5"}`}>
            <div className="group relative aspect-[3/4]">
              <img src={imgUrl(it.image_url)} alt={it.alt_text} className="w-full h-full object-cover" />
              <label className="absolute top-2 left-2 z-10 cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input
                  data-testid="portfolio-select"
                  type="checkbox"
                  checked={selected.has(it.id)}
                  onChange={() => toggleSel(it.id)}
                  className="accent-yellow-400 w-5 h-5 rounded"
                />
              </label>
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                <div className="flex justify-end gap-2">
                  <button data-testid="portfolio-edit" onClick={() => openEdit(it)} className="p-2 rounded-md bg-white/10 hover:bg-yellow-400 hover:text-black"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid="portfolio-delete" onClick={() => remove(it.id)} className="p-2 rounded-md bg-white/10 hover:bg-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="text-xs text-white font-medium">{it.title}</div>
              </div>
            </div>
            <div className="p-2 border-t border-white/5">
              <div className="text-[11px] text-zinc-400 truncate mb-1" title={it.title}>{it.title}</div>
              <select
                data-testid="portfolio-inline-category"
                value={it.category}
                onChange={(e) => changeCategory(it.id, e.target.value)}
                className="w-full bg-zinc-800 border border-white/10 rounded-md px-2 py-1.5 text-xs text-yellow-400 focus:border-yellow-400 focus:outline-none cursor-pointer"
              >
                {categories.map((c) => <option key={c.slug} value={c.slug} className="text-white bg-zinc-900">{c.name}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setModal(false)}>
          <div className="bg-[#18181B] border border-white/10 rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">{editing ? "Modifier" : "Nouvelle réalisation"}</h2>
              <button onClick={() => setModal(false)}><X className="w-5 h-5 text-zinc-400" /></button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <div onClick={() => fileRef.current?.click()} className="cursor-pointer border-2 border-dashed border-white/15 rounded-xl h-44 flex flex-col items-center justify-center overflow-hidden hover:border-yellow-400 transition-colors">
                {preview ? <img src={imgUrl(preview)} alt="preview" className="h-full object-contain" /> : (
                  <><Upload className="w-6 h-6 text-zinc-500 mb-2" /><span className="text-sm text-zinc-500">Cliquez pour choisir une image</span></>
                )}
              </div>
              <input ref={fileRef} data-testid="portfolio-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
              <input data-testid="portfolio-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="portfolio-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                  {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
                <input data-testid="portfolio-seo" value={form.seo_name} onChange={(e) => setForm({ ...form, seo_name: e.target.value })} placeholder="Nom SEO (fichier)" className={inputCls} />
              </div>
              <input data-testid="portfolio-alt" value={form.alt_text} onChange={(e) => setForm({ ...form, alt_text: e.target.value })} placeholder="Texte ALT (accessibilité & SEO)" className={inputCls} />
              <p className="text-xs text-zinc-500">L'image sera automatiquement compressée, redimensionnée et convertie en WebP pour optimiser le poids et le référencement.</p>
              <button data-testid="portfolio-save" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-yellow-400 text-black font-bold py-3 rounded-md hover:bg-yellow-300 disabled:opacity-60">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
